import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { Landmark, DollarSign, Calendar, ArrowRightLeft, Receipt } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/finance')({
  component: FinancePage,
});

function FinancePage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Finance</h1>
        <p className="text-muted-foreground mt-1">
          How revenue sharing and payouts work on Ludolio.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <DollarSign className="h-6 w-6 text-[hsl(var(--color-primary))]" />
          <div>
            <CardTitle>Revenue Share</CardTitle>
            <CardDescription>Simple and transparent split</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Ludolio uses a standard revenue share model similar to Steam and other major distribution platforms.
            You earn a percentage of the net revenue generated by your game through the platform.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><span className="font-medium text-foreground">70% to the Developer</span> — you keep the majority of every sale.</li>
            <li><span className="font-medium text-foreground">30% to Ludolio</span> — covers hosting, bandwidth, payment processing, and platform operations.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <Calendar className="h-6 w-6 text-[hsl(var(--color-primary))]" />
          <div>
            <CardTitle>Payout Schedule</CardTitle>
            <CardDescription>When and how you get paid</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Payouts are processed on a monthly cycle, similar to Steam. At the end of each calendar month,
            we tally your eligible earnings and initiate payment.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Payments are issued within <span className="font-medium text-foreground">30 days</span> after the month closes.</li>
            <li>You must reach a minimum balance of <span className="font-medium text-foreground">$100 USD</span> before a payout is triggered.</li>
            <li>Unpaid balances roll over to the next month automatically.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <ArrowRightLeft className="h-6 w-6 text-[hsl(var(--color-primary))]" />
          <div>
            <CardTitle>Payment Methods</CardTitle>
            <CardDescription>Bank transfers and tax information</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            Payouts are sent via direct bank transfer (ACH / wire). You will be asked to provide
            banking details and tax documentation once your first game is approved and begins generating revenue.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide a valid bank account in your country of residence.</li>
            <li>Submit a completed tax form (W-9 for US persons, W-8BEN for international).</li>
            <li>Ensure your account details are kept up to date to avoid payout delays.</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3">
          <Receipt className="h-6 w-6 text-[hsl(var(--color-primary))]" />
          <div>
            <CardTitle>Refunds & Chargebacks</CardTitle>
            <CardDescription>How adjustments are handled</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            In the event of a refund or chargeback, the corresponding amount is deducted from your
            next payout. We batch these adjustments so you can see them clearly in your monthly statement.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Refunds are granted according to our refund policy.</li>
            <li>Chargeback fees are shared between Ludolio and the developer.</li>
            <li>You will receive an itemised breakdown with each payout.</li>
          </ul>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-primary))]/8 p-4 text-sm text-muted-foreground">
        <div className="flex items-start gap-3">
          <Landmark className="h-5 w-5 text-[hsl(var(--color-primary))] mt-0.5" />
          <p>
            <span className="font-medium text-foreground">Note:</span> The finance portal is in early access.
            Full reporting and downloadable statements are coming in a future update. For urgent finance questions,
            contact us at <a href="mailto:ben@ludolio.com" className="underline text-foreground">ben@ludolio.com</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
